import {
  WorkspaceSelectorContext,
  WorkspaceBreadcrumbContext,
} from "./WorkspaceSelector";
import { ProofStudioBrand } from "./ProofStudioBrand";
import { AccountMenu, WorkspaceAccountContext } from "./AccountMenu";
import { LayoutGrid, ShieldCheck, UserRoundCog } from "lucide-react";
import { DisplayModeToggle } from "./Appearance";
import { EsmAccessContext } from "./FeatureAccess";
import { readPageLink } from "./page-links";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  lazy,
  Suspense,
  type ReactNode,
} from "react";
import { Select, Button, Control, Modal } from "./components";
import { TenantWorkspace } from "./TenantWorkspace";
import { AccountAdministration } from "./AccountAdministration";
import { AccountEmail } from "./AccountEmail";
import {
  AccountSecurity,
  AccountChallenge,
  ConfirmAccountPassword,
} from "./AccountSecurity";
import {
  accountRequest,
  unlockAccountSetup,
  accountNavigate,
  googleStart,
  AccountRequestError,
  accountMessages,
  type AccountView,
  type AdministrationView,
} from "./account-api";
import "./accounts.css";
const NativePreview = lazy(() =>
  import("./NativePreview").then((module) => ({
    default: module.NativePreview,
  })),
);
const Impersonation = lazy(() =>
  import("./Impersonation").then((module) => ({
    default: module.Impersonation,
  })),
);
function AccessCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="instance-shell account-access">
      <header className="instance-brand">
        Proof Studio <span>Document workspace</span>
      </header>
      <section className="instance-panel">
        <DisplayModeToggle />
        <h1>{title}</h1>
        {children}
        <nav className="account-public-links" aria-label="Public documentation">
          <a href="/help">Guides & help</a>
          <a href="/docs">API integration</a>
          <a href="/docs/scalar">Scalar</a>
        </nav>
      </section>
    </main>
  );
}
export function ApplicationAccounts() {
  const [view, setView] = useState<AccountView>(),
    [administration, setAdministration] = useState<AdministrationView>(),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [path, setPath] = useState(location.pathname),
    [codes, setCodes] = useState<string[]>(),
    [loadFailed, setLoadFailed] = useState(false);
  const [breadcrumbHost, setBreadcrumbHost] = useState<HTMLDivElement | null>(
    null,
  );
  const operation = useRef(false),
    beforeLeave = useRef<() => Promise<void>>(async () => {}),
    lastScope = useRef("");
  const refresh = useCallback(async () => {
    try {
      const next = await accountRequest<AccountView>("/status");
      setView(next);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);
  const refreshAdministration = useCallback(async () => {
    setAdministration(
      await accountRequest<AdministrationView>("/administration"),
    );
  }, []);
  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), 5000),
      navigate = () => {
        setPath(location.pathname);
        setError("");
        setNotice("");
      };
    window.addEventListener("popstate", navigate);
    return () => {
      clearInterval(timer);
      window.removeEventListener("popstate", navigate);
    };
  }, [refresh]);
  const run = useCallback(
    async (work: () => Promise<unknown>, success?: string) => {
      if (operation.current) return;
      operation.current = true;
      setBusy(true);
      setError("");
      setNotice("");
      try {
        await work();
        if (success) setNotice(success);
        await refresh();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Request failed.");
        if (
          cause instanceof AccountRequestError &&
          [401, 403].includes(cause.status)
        )
          await refresh();
      } finally {
        operation.current = false;
        setBusy(false);
      }
    },
    [refresh],
  );
  const mayAdmin = !!(
    view?.user?.root ||
    view?.workspaces.some((site) =>
      site.companies.some((company) =>
        company.roleIds.includes("tenant.administrator"),
      ),
    )
  );
  useEffect(() => {
    if (
      view?.user &&
      (view.setupAuthorized || (path === "/administration" && mayAdmin)) &&
      view.twoFactor?.satisfied
    )
      void refreshAdministration().catch((cause) => setError(cause.message));
  }, [
    view?.user?.id,
    view?.setupAuthorized,
    view?.twoFactor?.satisfied,
    path,
    mayAdmin,
    refreshAdministration,
  ]);
  const registerBeforeLeave = useCallback((handler: () => Promise<void>) => {
    beforeLeave.current = handler;
    return () => {
      if (beforeLeave.current === handler) beforeLeave.current = async () => {};
    };
  }, []);
  const leave = useCallback(async () => {
    await beforeLeave.current();
    await accountRequest("/logout", {});
    setAdministration(undefined);
    accountNavigate("/login");
    await refresh();
  }, [refresh]);
  const accountAccess = useMemo(
    () => ({
      onLeave: () => void run(leave),
      onExpired: () => void refresh(),
      name: view?.user?.name,
    }),
    [run, leave, refresh, view?.user?.name],
  );
  const navigate = (target: string) =>
    void run(async () => {
      await beforeLeave.current();
      accountNavigate(target);
    });
  const linkedTenant = readPageLink()?.tenant;
  const linkedCompany = view?.workspaces
    .flatMap((site) =>
      site.companies.map((company) => ({ ...company, siteId: site.id })),
    )
    .find((company) => company.tenantId === linkedTenant);
  const changingLinkedWorkspace = !!(
    view?.user &&
    linkedCompany &&
    view.selection?.companyId !== linkedCompany.id
  );
  const saveLinkedWorkspace = beforeLeave.current;
  useEffect(() => {
    if (!changingLinkedWorkspace || !linkedCompany) return;
    void run(async () => {
      await saveLinkedWorkspace();
      await accountRequest("/workspace", {
        siteId: linkedCompany.siteId,
        companyId: linkedCompany.id,
      });
    });
  }, [changingLinkedWorkspace, linkedCompany?.id]);
  if (!view)
    return (
      <AccessCard
        title={loadFailed ? "Account service unavailable" : "Checking access…"}
      >
        {loadFailed && (
          <Button onClick={() => void refresh()}>Retry account access</Button>
        )}
      </AccessCard>
    );
  const feedback = (
    <div aria-live="polite">
      {error && (
        <p role="alert" className="error-text account-feedback">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="account-feedback">
          {notice}
        </p>
      )}
      {busy && <p role="status">Working…</p>}
    </div>
  );
  if (view.user && linkedTenant && !linkedCompany)
    return (
      <AccessCard title="Linked workspace unavailable">
        <p>You do not have access to the site and company in this link.</p>
        <Button
          onClick={() => {
            history.replaceState(null, "", "/");
            void refresh();
          }}
        >
          Open your workspace
        </Button>
      </AccessCard>
    );
  const recovery = codes && (
    <Modal title="Save your recovery codes" onClose={() => setCodes(undefined)}>
      <p>
        Each code can be used once if your email or authenticator is
        unavailable. Save these codes privately; they will not be shown again.
      </p>
      <div className="account-recovery">
        {codes.map((code) => (
          <code key={code}>{code}</code>
        ))}
      </div>
      <div className="button-row">
        <Button
          onClick={() => {
            const blob = new Blob([codes.join("\n") + "\n"], {
                type: "text/plain",
              }),
              url = URL.createObjectURL(blob),
              anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = "proof-studio-recovery-codes.txt";
            anchor.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Download recovery codes
        </Button>
        <Button kind="primary" onClick={() => setCodes(undefined)}>
          I've saved these codes
        </Button>
      </div>
    </Modal>
  );
  if (!view.configured)
    return (
      <AccessCard title="Set up Proof Studio">
        {feedback}
        <fieldset disabled={busy} className="instance-form-fields">
          <AccountSetup
            view={view}
            administration={administration}
            run={run}
            refreshAdministration={refreshAdministration}
          />
        </fieldset>
      </AccessCard>
    );
  if (path === "/accept-invitation")
    return (
      <AccessCard title="Accept your invitation">
        {feedback}
        <InvitationAcceptance run={run} />
      </AccessCard>
    );
  if (path === "/login/magic")
    return (
      <AccessCard title="Sign in with your email link">
        {feedback}
        <MagicLinkConfirmation run={run} busy={busy} />
      </AccessCard>
    );
  if (path === "/reset-password")
    return (
      <AccessCard title="Reset your password">
        {feedback}
        <ResetPassword run={run} />
      </AccessCard>
    );
  if (!view.user)
    return (
      <AccessCard title="Sign in">
        {feedback}
        <fieldset disabled={busy} className="instance-form-fields">
          <AccountLogin
            googleAvailable={view.googleLoginAvailable}
            emailAvailable={view.emailLoginAvailable}
            run={run}
          />
        </fieldset>
      </AccessCard>
    );
  if (view.twoFactor?.required && !view.twoFactor.satisfied)
    return (
      <AccessCard title="Two-factor verification">
        {feedback}
        <fieldset disabled={busy} className="instance-form-fields">
          <Control label="Verify access for company">
            <Select
              value={`${view.selection?.siteId || ""}:${view.selection?.companyId || ""}`}
              onChange={(event) => {
                const [siteId, companyId] = event.target.value.split(":");
                void run(async () => {
                  await accountRequest("/workspace", { siteId, companyId });
                });
              }}
            >
              {view.workspaces.flatMap((site) =>
                site.companies.map((company) => (
                  <option key={company.id} value={`${site.id}:${company.id}`}>
                    {site.name} / {company.name}
                  </option>
                )),
              )}
            </Select>
          </Control>
          <AccountChallenge view={view} run={run} onRecovery={setCodes} />
        </fieldset>
        {recovery}
      </AccessCard>
    );
  const selection = `${view.selection?.siteId || ""}:${view.selection?.companyId || ""}`;
  lastScope.current = selection;
  const workspaceSelector = (
    <label className="company-workspace-selector">
      <span>Workspace (company)</span>
      <Select
        aria-label="Active site and company"
        disabled={busy}
        value={selection}
        onChange={(event) => {
          const selected = view.workspaces
            .flatMap((site) =>
              site.companies.map((company) => ({
                key: `${site.id}:${company.id}`,
                siteId: site.id,
                companyId: company.id,
              })),
            )
            .find((company) => company.key === event.target.value);
          if (selected)
            void run(async () => {
              await beforeLeave.current();
              await accountRequest("/workspace", {
                siteId: selected.siteId,
                companyId: selected.companyId,
              });
              setAdministration(undefined);
              history.replaceState(
                null,
                "",
                location.pathname + location.search,
              );
              accountNavigate("/");
            });
        }}
      >
        {view.workspaces.map((site) => (
          <optgroup key={site.id} label={site.name}>
            {site.companies.map((company) => (
              <option key={company.id} value={`${site.id}:${company.id}`}>
                {site.name} / {company.name}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
    </label>
  );
  const inWorkspace =
    !!view.selection &&
    !["/administration", "/account", "/proof-preview", "/impersonation"].includes(
      path,
    );
  const accountMenu = (placement: "up" | "down") => (
    <AccountMenu
      name={view.user!.name}
      detail={view.user!.email}
      placement={placement}
      items={[
        {
          label: "Workspace",
          Icon: LayoutGrid,
          current: path !== "/administration" && path !== "/account",
          onSelect: () => navigate("/"),
        },
        ...(mayAdmin
          ? [
              {
                label: "Administration",
                Icon: ShieldCheck,
                current: path === "/administration",
                onSelect: () => navigate("/administration"),
              },
            ]
          : []),
        {
          label: "Account & security",
          Icon: UserRoundCog,
          current: path === "/account",
          onSelect: () => navigate("/account"),
        },
      ]}
      onSignOut={() => void run(leave)}
    />
  );
  return (
    <WorkspaceSelectorContext.Provider value={workspaceSelector}>
      <WorkspaceAccountContext.Provider
        value={inWorkspace ? accountMenu("up") : null}
      >
      <WorkspaceBreadcrumbContext.Provider value={breadcrumbHost}>
        <div className="application-account-shell" aria-busy={busy}>
          <header className="application-account-bar">
            <ProofStudioBrand
              authenticated
              onClick={(event) => {
                event.preventDefault();
                void run(async () => {
                  await beforeLeave.current();
                  location.assign("/#studio?section=projects");
                });
              }}
            />

            <div className="account-location" ref={setBreadcrumbHost} />
            {!inWorkspace && (
              <nav aria-label="Account navigation">{accountMenu("down")}</nav>
            )}
          </header>
          {(path === "/administration" || path === "/account") && (
            <div className="account-workspace-selector">
              {workspaceSelector}
            </div>
          )}
          {(error || notice || busy) && (
            <div className="account-global-feedback">{feedback}</div>
          )}
          {path === "/account" ? (
            <AccountSecurity view={view} run={run} onRecovery={setCodes} />
          ) : path === "/administration" ? (
            !mayAdmin ? (
              <main className="account-content">
                <h1>Administration access required</h1>
                <p>
                  Your account can access its assigned workspace. Contact an
                  administrator to manage configuration.
                </p>
              </main>
            ) : administration ? (
              <AccountAdministration
                key={view.user.id}
                data={administration}
                run={run}
                refresh={refreshAdministration}
              />
            ) : (
              <main className="account-content">
                <h1>Administration</h1>
                <p>Loading authorized configuration…</p>
              </main>
            )
          ) : path === "/proof-preview" ? (
            <Suspense fallback={<p>Opening proof…</p>}>
              <NativePreview />
            </Suspense>
          ) : path === "/impersonation" ? (
            <Suspense fallback={<p>Opening proof…</p>}>
              <Impersonation />
            </Suspense>
          ) : view.selection ? (
            <EsmAccessContext.Provider value={view.features?.esm === true}>
              <TenantWorkspace
                key={`${view.user.id}:${selection}`}
                accountAccess={accountAccess}
                registerBeforeLeave={registerBeforeLeave}
              />
            </EsmAccessContext.Provider>
          ) : (
            <main className="account-content">
              <h1>No assigned company</h1>
              <p>
                Ask a site or application administrator to assign your account
                to a company.
              </p>
            </main>
          )}
          {recovery}
        </div>
      </WorkspaceBreadcrumbContext.Provider>
      </WorkspaceAccountContext.Provider>
    </WorkspaceSelectorContext.Provider>
  );
}
function AccountSetup({
  view,
  administration,
  run,
  refreshAdministration,
}: {
  view: AccountView;
  administration?: AdministrationView;
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
  refreshAdministration: () => Promise<void>;
}) {
  const [token, setToken] = useState(""),
    [name, setName] = useState(""),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState(""),
    [siteName, setSiteName] = useState("My site"),
    [companyName, setCompanyName] = useState("My company"),
    [seed, setSeed] = useState(
      () => sessionStorage.getItem("proof-initial-seed") === "true",
    ),
    [emailLater, setEmailLater] = useState(false),
    [step, setStep] = useState(() => {
      const value = Number(sessionStorage.getItem("proof-initial-step") || 3);
      return [3, 4, 5].includes(value) ? value : 3;
    });
  const next = (step: number) => {
    setStep(step);
    sessionStorage.setItem("proof-initial-step", String(step));
  };
  const current = !view.setupAuthorized ? 1 : !view.hasAdministrator ? 2 : step;
  return (
    <>
      <ol className="account-setup-steps" aria-label="Setup steps">
        {["Access token", "Administrator", "Demo data", "Email", "Review"].map(
          (name, index) => (
            <li
              key={name}
              aria-current={index + 1 === current ? "step" : undefined}
            >
              {index + 1}. {name}
            </li>
          ),
        )}
      </ol>
      {current === 1 && (
        <>
          {" "}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await unlockAccountSetup(token);
                setToken("");
              }, "Setup unlocked.");
            }}
          >
            <h2>Enter the initial access token</h2>
            <p>
              The instance administrator can read this one-use token from the
              private <code>application-accounts/setup-access.token</code> file
              in the server's data directory, or the configured{" "}
              <code>PROOF_ACCESS_TOKEN_FILE</code>. The token never belongs in a
              URL.
            </p>
            <details className="account-token-help">
              <summary>Get the access token from Kubernetes</summary>
              <p>
                Run this using your authorized cluster connection for the Proof
                Studio deployment:
              </p>
              <pre>
                <code>
                  kubectl exec -n proofing deployment/proofing -- cat
                  /var/lib/proof/data/application-accounts/setup-access.token
                </code>
              </pre>
              <small>
                Copy the returned value into the field below. The command
                applies before setup is completed; keep its output private.
              </small>
            </details>
            <Control label="Initial access token">
              <input
                type="password"
                required
                autoComplete="off"
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
            </Control>
            <Button kind="primary" type="submit">
              Unlock setup
            </Button>
          </form>
          {view.hasAdministrator && (
            <details className="account-token-help">
              <summary>Resume setup using your administrator account</summary>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(async () => {
                    await accountRequest("/login", { email, password });
                    setPassword("");
                  }, "Administrator setup resumed.");
                }}
              >
                <p>
                  Sign in with the administrator already created for this
                  instance.
                </p>
                <Control label="Administrator email to resume">
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Control>
                <Control label="Administrator password to resume">
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Control>
                <Button type="submit">Resume setup</Button>
              </form>
            </details>
          )}
        </>
      )}
      {current >= 3 && (
        <details className="account-token-help">
          <summary>Confirm password for sensitive setup changes</summary>
          <p>
            If setup takes more than ten minutes, confirm the administrator
            password before changing email credentials.
          </p>
          <ConfirmAccountPassword run={run} />
        </details>
      )}
      {current === 2 && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (password !== confirm)
                throw new Error("The passwords do not match.");
              await accountRequest("/setup/administrator", {
                name,
                email,
                password,
                siteName,
                companyName,
              });
              setPassword("");
              setConfirm("");
              next(3);
            }, "Administrator created. Continue with your initial settings.");
          }}
        >
          <h2>Create the administrator account</h2>
          <p>
            This account can manage application defaults, all sites and all
            companies. No account or password is created automatically.
          </p>
          <div className="control-grid">
            <Control label="Administrator name">
              <input
                required
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Control>
            <Control label="Administrator email">
              <input
                required
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Control>
            <Control label="Administrator password">
              <input
                required
                type="password"
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Control>
            <Control label="Confirm administrator password">
              <input
                required
                type="password"
                minLength={12}
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </Control>
            <Control label="Initial site name">
              <input
                required
                value={siteName}
                onChange={(event) => setSiteName(event.target.value)}
              />
            </Control>
            <Control label="Initial company name">
              <input
                required
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
              />
            </Control>
          </div>
          <Button kind="primary" type="submit">
            Create administrator
          </Button>
        </form>
      )}
      {current === 3 && (
        <section>
          <h2>Would you like demo projects?</h2>
          <p>
            Start with an empty workspace, or add example projects to explore
            forms, document design and proof generation. Existing documents and
            resources are retained in either case.
          </p>
          <label className="account-check">
            <input
              type="checkbox"
              checked={seed}
              onChange={(event) => {
                setSeed(event.target.checked);
                sessionStorage.setItem(
                  "proof-initial-seed",
                  String(event.target.checked),
                );
              }}
            />{" "}
            Seed demo projects
          </label>
          <Button
            kind="primary"
            onClick={() => {
              next(4);
              void run(refreshAdministration);
            }}
          >
            Continue to email setup
          </Button>
        </section>
      )}
      {current === 4 && (
        <section>
          {administration ? (
            <AccountEmail
              administration={administration}
              run={run}
              refresh={refreshAdministration}
              setup
            />
          ) : (
            <>
              <p>Loading email configuration…</p>
              <Button onClick={() => void run(refreshAdministration)}>
                Load email settings
              </Button>
            </>
          )}
          <label className="account-check">
            <input
              type="checkbox"
              checked={emailLater}
              onChange={(event) => setEmailLater(event.target.checked)}
            />{" "}
            Configure email later
          </label>
          <p className="muted">
            Invitations, password-reset emails and email verification codes need
            a configured sender. Authenticator apps can be configured
            independently.
          </p>
          <div className="button-row">
            <Button onClick={() => next(3)}>Back to demo data</Button>
            <Button kind="primary" onClick={() => next(5)}>
              Review initial configuration
            </Button>
          </div>
        </section>
      )}
      {current === 5 && (
        <section>
          <h2>Review and finish setup</h2>
          <dl className="account-review">
            <dt>Administrator</dt>
            <dd>
              {view.user?.name} · {view.user?.email}
            </dd>
            <dt>Demo data</dt>
            <dd>
              {seed ? "Add example projects" : "Do not add demo projects"}
            </dd>
            <dt>Email</dt>
            <dd>
              {emailLater
                ? "Configure later"
                : administration?.mail.find(
                    (mail) =>
                      mail.id === administration.settings?.defaultMailId,
                  )?.name || "A ready default email connection is required"}
            </dd>
          </dl>
          <div className="button-row">
            <Button onClick={() => next(4)}>Back to email</Button>
            <Button
              kind="primary"
              onClick={() =>
                void run(async () => {
                  await accountRequest("/setup/complete", {
                    seedDemo: seed,
                    emailLater,
                  });
                  sessionStorage.removeItem("proof-initial-step");
                  sessionStorage.removeItem("proof-initial-seed");
                  accountNavigate("/");
                }, "Initial setup complete.")
              }
            >
              Finish setup
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
function MagicLinkConfirmation({
  run,
  busy,
}: {
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
  busy: boolean;
}) {
  const [token] = useState(() => location.hash.slice(1));
  useEffect(() => {
    history.replaceState(null, "", location.pathname);
  }, []);
  return (
    <div className="account-login">
      <p>
        Confirm to sign in. Links expire after 15 minutes and can only be used
        once.
      </p>
      <Button
        kind="primary"
        disabled={busy || !token}
        onClick={() =>
          void run(async () => {
            await accountRequest("/magic-link/consume", { token });
            accountNavigate("/");
          })
        }
      >
        Confirm sign in
      </Button>
      <Button disabled={busy} onClick={() => accountNavigate("/login")}>
        Back to sign in
      </Button>
    </div>
  );
}
function AccountLogin({
  googleAvailable,
  emailAvailable,
  run,
}: {
  googleAvailable: boolean;
  emailAvailable: boolean;
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
}) {
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [reset, setReset] = useState(false),
    [magic, setMagic] = useState(false);
  const oauthError = new URLSearchParams(location.search).get("authError");
  return (
    <>
      {oauthError && (
        <p role="alert" className="error-text">
          {accountMessages[oauthError] ||
            "Google authorization was not completed. Try signing in with your password."}
        </p>
      )}
      <form
        className="account-login"
        onSubmit={(event) => {
          event.preventDefault();
          void run(
            async () => {
              if (magic) await accountRequest("/magic-link/request", { email });
              else if (reset)
                await accountRequest("/password/request-reset", { email });
              else {
                await accountRequest("/login", { email, password });
                setPassword("");
                if (location.pathname === "/login") accountNavigate("/");
              }
            },
            magic
              ? "If that account can receive email, a sign-in link will arrive shortly."
              : reset
                ? "If that account can receive email, a password-reset link will arrive shortly."
                : undefined,
          );
        }}
      >
        <Control label="Email address">
          <input
            required
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Control>
        {!reset && !magic && (
          <Control label="Password">
            <input
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Control>
        )}
        <div className="button-row">
          <Button type="submit" kind="primary">
            {magic
              ? "Email sign-in link"
              : reset
                ? "Send reset link"
                : "Sign in"}
          </Button>
          <Button
            onClick={() => {
              setReset(!reset);
              setMagic(false);
              setPassword("");
            }}
          >
            {reset ? "Back to sign in" : "Forgot password?"}
          </Button>
        </div>
      </form>
      {!reset && emailAvailable && (
        <Button
          onClick={() => {
            setMagic(!magic);
            setPassword("");
          }}
        >
          {magic ? "Use password instead" : "Sign in with an email link"}
        </Button>
      )}
      {!reset && !magic && googleAvailable && (
        <Button onClick={() => void run(() => googleStart("login"))}>
          Sign in with Google
        </Button>
      )}
    </>
  );
}
function InvitationAcceptance({
  run,
}: {
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
}) {
  const [token] = useState(() => location.hash.slice(1)),
    [invitation, setInvitation] = useState<{
      email: string;
      name: string;
      existingAccount: boolean;
    }>(),
    [error, setError] = useState(""),
    [name, setName] = useState(""),
    [password, setPassword] = useState("");
  useEffect(() => {
    history.replaceState(null, "", location.pathname);
    void accountRequest<typeof invitation>("/invitations/preview", { token })
      .then((result) => {
        setInvitation(result);
        setName(result?.name || "");
      })
      .catch((cause) => setError(cause.message));
  }, [token]);
  return (
    <>
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      {invitation && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await accountRequest("/invitations/accept", {
                token,
                name,
                password,
              });
              setPassword("");
              accountNavigate("/");
            }, "Invitation accepted.");
          }}
        >
          <p>
            Join with {invitation.email}.{" "}
            {invitation.existingAccount
              ? "Enter your existing account password; it will not be replaced."
              : "You must set a password before your new account can sign in."}
          </p>
          <Control label="Your name">
            <input
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Control>
          <Control
            label={
              invitation.existingAccount
                ? "Existing account password"
                : "Choose your password"
            }
          >
            <input
              required
              minLength={12}
              maxLength={128}
              type="password"
              autoComplete={
                invitation.existingAccount ? "current-password" : "new-password"
              }
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Control>
          <Button type="submit" kind="primary">
            Accept invitation
          </Button>
        </form>
      )}
      <Button onClick={() => accountNavigate("/login")}>Go to sign in</Button>
    </>
  );
}
function ResetPassword({
  run,
}: {
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
}) {
  const [token] = useState(() => location.hash.slice(1)),
    [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState("");
  useEffect(() => {
    history.replaceState(null, "", location.pathname);
  }, []);
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void run(async () => {
          if (password !== confirm)
            throw new Error("The passwords do not match.");
          await accountRequest("/password/reset", { token, password });
          setPassword("");
          setConfirm("");
          accountNavigate("/login");
        }, "Password reset. Sign in with your new password; your two-factor settings remain enabled.");
      }}
    >
      <Control label="New password">
        <input
          type="password"
          required
          minLength={12}
          maxLength={128}
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Control>
      <Control label="Confirm new password">
        <input
          type="password"
          required
          minLength={12}
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </Control>
      <Button type="submit" kind="primary">
        Reset password
      </Button>
    </form>
  );
}
